import { useEffect, useState } from "react";
import { X, Trash2, Plus, MessageSquare, Calendar, Flag, Tag, CheckSquare, Loader2, Send, History, Repeat, BookmarkPlus, Lock } from "lucide-react";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { toast } from "sonner";
import { Calendar as CalendarPicker } from "./ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import api, { formatApiErrorDetail } from "../lib/api";
import { realtime } from "../lib/ws";
import { useAuth } from "../context/AuthContext";
import { ConfirmDialog } from "./ConfirmDialog";
import { Avatar, PRIORITIES, LABEL_COLORS, statusRule } from "../lib/helpers";
import { TimeTracker } from "./TimeTracker";
import { Dependencies } from "./Dependencies";
import { Attachments } from "./Attachments";

export default function TaskModal({ task: initial, board, users, onClose, onUpdated, onDeleted }) {
  const { user } = useAuth();
  const [task, setTask] = useState(initial);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [newSub, setNewSub] = useState("");
  const [newLabel, setNewLabel] = useState("");
  const [posting, setPosting] = useState(false);
  const [tab, setTab] = useState("comments");
  const [activity, setActivity] = useState([]);
  const [mentionIds, setMentionIds] = useState([]);
  const [mentionQuery, setMentionQuery] = useState(null);
  const [reactingId, setReactingId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [dueOpen, setDueOpen] = useState(false);
  const [startOpen, setStartOpen] = useState(false);
  const [warnMsg, setWarnMsg] = useState(null);
  const [pendingStatus, setPendingStatus] = useState(null);

  useEffect(() => {
    api.get("/departments").then((r) => setDepartments(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    api.get(`/tasks/${initial.id}/comments`).then((r) => setComments(r.data)).catch(() => {});
  }, [initial.id]);

  useEffect(() => {
    if (tab === "activity") {
      api.get(`/tasks/${initial.id}/activity`).then((r) => setActivity(r.data)).catch(() => {});
    }
  }, [tab, initial.id, task]);

  const save = async (patch) => {
    const prev = task;
    const updated = { ...task, ...patch };
    setTask(updated);
    try {
      const { data } = await api.put(`/tasks/${task.id}`, patch);
      setTask(data);
      onUpdated(data);
    } catch (e) {
      setTask(prev);
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "No se pudo guardar");
    }
  };

  const del = async () => {
    setConfirmDelete(false);
    try {
      await api.delete(`/tasks/${task.id}`);
      onDeleted(task.id);
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "No se pudo eliminar");
    }
  };

  const addComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;
    setPosting(true);
    try {
      const { data } = await api.post(`/tasks/${task.id}/comments`, { text: newComment, mentions: mentionIds });
      setComments((c) => [...c, data]);
      setNewComment("");
      setMentionIds([]);
    } finally { setPosting(false); }
  };

  const toggleAssignee = (uid) => {
    const set = new Set(task.assignee_ids || []);
    set.has(uid) ? set.delete(uid) : set.add(uid);
    save({ assignee_ids: [...set] });
  };

  const addSub = () => {
    if (!newSub.trim()) return;
    const subs = [...(task.subtasks || []), { id: crypto.randomUUID(), title: newSub.trim(), done: false }];
    save({ subtasks: subs });
    setNewSub("");
  };
  const toggleSub = (id) => save({ subtasks: task.subtasks.map((s) => s.id === id ? { ...s, done: !s.done } : s) });
  const removeSub = (id) => save({ subtasks: task.subtasks.filter((s) => s.id !== id) });

  const addLabel = () => {
    if (!newLabel.trim()) return;
    save({ labels: [...(task.labels || []), newLabel.trim()] });
    setNewLabel("");
  };
  const removeLabel = (l) => save({ labels: task.labels.filter((x) => x !== l) });

  const labelColor = (l) => LABEL_COLORS[l.split("").reduce((a, c) => a + c.charCodeAt(0), 0) % LABEL_COLORS.length];
  const doneSubs = (task.subtasks || []).filter((s) => s.done).length;

  const QUICK_EMOJIS = ["👍", "❤️", "😂", "🎉", "👀", "✅"];
  const onCommentChange = (e) => {
    const v = e.target.value;
    setNewComment(v);
    const m = v.match(/@([\wÀ-ÿ]*)$/);
    setMentionQuery(m ? m[1].toLowerCase() : null);
  };
  const pickMention = (u) => {
    setNewComment((c) => c.replace(/@([\wÀ-ÿ]*)$/, `@${u.name} `));
    setMentionIds((ids) => [...new Set([...ids, u.id])]);
    setMentionQuery(null);
  };
  const react = async (cid, emoji) => {
    setReactingId(null);
    try {
      const { data } = await api.post(`/comments/${cid}/react`, { emoji });
      setComments((cs) => cs.map((c) => c.id === cid ? { ...c, reactions: data.reactions } : c));
    } catch (e) { console.error("[task] no se pudo reaccionar", e); }
  };
  const renderText = (text) => {
    const names = users.map((u) => u.name).filter(Boolean).sort((a, b) => b.length - a.length);
    if (!names.length) return text;
    const re = new RegExp(`@(${names.map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|")})`, "g");
    return text.split(re).map((p, i) => names.includes(p) ? <span key={`${i}-${p}`} className="text-primary font-semibold">@{p}</span> : p);
  };
  const mentionMatches = mentionQuery !== null ? users.filter((u) => u.name.toLowerCase().includes(mentionQuery)).slice(0, 5) : [];
  const currentCol = board.columns.find((c) => c.id === task.status);
  const isInProgress = !!currentCol && currentCol.title.toLowerCase().includes("progreso");

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-0 sm:p-4" onClick={onClose}>
      <div className="w-full max-w-2xl h-full sm:h-auto max-h-full sm:max-h-[90vh] overflow-y-auto bg-[#121214] border border-white/10 rounded-none sm:rounded-2xl animate-pop" onClick={(e) => e.stopPropagation()} data-testid="task-modal">
        <div className="sticky top-0 z-10 bg-[#121214] flex items-start justify-between gap-4 p-6 border-b border-white/10">
          <input
            data-testid="task-title-input"
            value={task.title}
            onChange={(e) => setTask({ ...task, title: e.target.value })}
            onBlur={(e) => e.target.value !== initial.title && save({ title: e.target.value })}
            className="flex-1 bg-transparent font-display text-2xl font-bold outline-none focus:bg-white/5 rounded px-1"
          />
          <div className="flex items-center gap-2">
            <button onClick={async () => { try { await api.post("/task-templates", { name: task.title, title: task.title, description: task.description || "", priority: task.priority, labels: task.labels || [], subtasks: task.subtasks || [] }); toast.success("Guardada como plantilla"); } catch { toast.error("No se pudo guardar"); } }}
              data-testid="save-task-template" title="Guardar como plantilla" className="text-zinc-500 hover:text-[#FFD600] transition-colors"><BookmarkPlus className="h-5 w-5" /></button>
            <button onClick={() => setConfirmDelete(true)} data-testid="delete-task-button" className="text-zinc-500 hover:text-red-400 transition-colors"><Trash2 className="h-5 w-5" /></button>
            <button onClick={onClose} data-testid="close-task-modal" className="text-zinc-500 hover:text-white transition-colors"><X className="h-5 w-5" /></button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Meta row */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <div>
              <label className="text-xs uppercase tracking-wider font-semibold text-zinc-500 flex items-center gap-1.5"><Flag className="h-3 w-3" /> Estado</label>
              <select data-testid="task-status-select" value={task.status} onChange={(e) => {
                const rule = statusRule(board.columns, task.status, e.target.value);
                if (rule === "block-back") { setWarnMsg("La tarea ya fue iniciada y no puede regresar al estado «Planeado»."); return; }
                if (rule === "confirm-start") { setPendingStatus(e.target.value); return; }
                save({ status: e.target.value });
              }}
                className="mt-1.5 w-full bg-[#0A0A0B] border border-white/10 rounded-lg px-2.5 py-2 text-sm outline-none focus:border-primary">
                {board.columns.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider font-semibold text-zinc-500 flex items-center gap-1.5"><Flag className="h-3 w-3" /> Prioridad</label>
              <select data-testid="task-priority-select" value={task.priority} onChange={(e) => save({ priority: e.target.value })}
                className="mt-1.5 w-full bg-[#0A0A0B] border border-white/10 rounded-lg px-2.5 py-2 text-sm outline-none focus:border-primary">
                {Object.entries(PRIORITIES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider font-semibold text-zinc-500 flex items-center gap-1.5"><Calendar className="h-3 w-3" /> Vencimiento</label>
              {(() => {
                const canEditDue = user?.role === "admin" || task.created_by === user?.id;
                const dueDate = task.due_date ? new Date(task.due_date.slice(0, 10) + "T00:00:00") : null;
                return (
                  <>
                    <Popover open={dueOpen} onOpenChange={setDueOpen}>
                      <PopoverTrigger asChild>
                        <button type="button" data-testid="task-due-input" disabled={!canEditDue}
                          title={canEditDue ? "" : "Solo el creador de la tarea o un administrador pueden cambiarla"}
                          className={`mt-1.5 w-full bg-[#0A0A0B] border border-white/10 rounded-lg px-2.5 py-2 text-sm outline-none focus:border-primary flex items-center gap-2 text-left disabled:opacity-50 disabled:cursor-not-allowed ${dueDate ? "" : "text-zinc-500"}`}>
                          <Calendar className="h-3.5 w-3.5 text-zinc-500 shrink-0" />
                          <span className="truncate capitalize">
                            {dueDate ? format(dueDate, "EEE, d MMM yyyy", { locale: es }) : "Elegir fecha"}
                          </span>
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0 z-[100] bg-[#121214] border-white/10 text-white" align="start">
                        <CalendarPicker mode="single" selected={dueDate} onSelect={(d) => { setDueOpen(false); if (!d) return; const today = new Date(); today.setHours(0, 0, 0, 0); if (d < today) { setWarnMsg("No puedes elegir una fecha de vencimiento anterior a la fecha actual."); return; } save({ due_date: format(d, "yyyy-MM-dd") }); }} locale={es} initialFocus />
                        {dueDate && (
                          <button type="button" onClick={() => { save({ due_date: null }); setDueOpen(false); }} data-testid="task-clear-due"
                            className="w-full text-xs text-zinc-400 hover:text-white py-2.5 border-t border-white/10 transition-colors">
                            Quitar fecha
                          </button>
                        )}
                      </PopoverContent>
                    </Popover>
                    {!canEditDue && <p className="text-[10px] text-zinc-600 mt-1 flex items-center gap-1" data-testid="due-locked-note"><Lock className="h-2.5 w-2.5" /> Solo el creador puede cambiarla</p>}
                  </>
                );
              })()}
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider font-semibold text-zinc-500 flex items-center gap-1.5"><Calendar className="h-3 w-3" /> Inicio</label>
              {(() => {
                const startDate = task.start_date ? new Date(task.start_date.slice(0, 10) + "T00:00:00") : null;
                return (
                  <Popover open={startOpen} onOpenChange={setStartOpen}>
                    <PopoverTrigger asChild>
                      <button type="button" data-testid="task-start-input"
                        className={`mt-1.5 w-full bg-[#0A0A0B] border border-white/10 rounded-lg px-2.5 py-2 text-sm outline-none focus:border-primary flex items-center gap-2 text-left ${startDate ? "" : "text-zinc-500"}`}>
                        <Calendar className="h-3.5 w-3.5 text-zinc-500 shrink-0" />
                        <span className="truncate capitalize">
                          {startDate ? format(startDate, "EEE, d MMM yyyy", { locale: es }) : "Elegir fecha"}
                        </span>
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 z-[100] bg-[#121214] border-white/10 text-white" align="start">
                      <CalendarPicker mode="single" selected={startDate} onSelect={(d) => { save({ start_date: d ? format(d, "yyyy-MM-dd") : null }); setStartOpen(false); }} locale={es} initialFocus />
                      {startDate && (
                        <button type="button" onClick={() => { save({ start_date: null }); setStartOpen(false); }} data-testid="task-clear-start"
                          className="w-full text-xs text-zinc-400 hover:text-white py-2.5 border-t border-white/10 transition-colors">
                          Quitar fecha
                        </button>
                      )}
                    </PopoverContent>
                  </Popover>
                );
              })()}
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider font-semibold text-zinc-500 flex items-center gap-1.5"><Repeat className="h-3 w-3" /> Recurrencia</label>
              <select data-testid="task-recurrence-select" value={task.recurrence || "none"} onChange={(e) => save({ recurrence: e.target.value })}
                className="mt-1.5 w-full bg-[#0A0A0B] border border-white/10 rounded-lg px-2.5 py-2 text-sm outline-none focus:border-primary">
                <option value="none">Ninguna</option>
                <option value="daily">Diaria</option>
                <option value="weekly">Semanal</option>
                <option value="monthly">Mensual</option>
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider font-semibold text-zinc-500">Estimación (h)</label>
              <input data-testid="task-estimate-input" type="number" step="0.5" min="0" defaultValue={task.estimate_hours || ""}
                onBlur={(e) => save({ estimate_hours: e.target.value ? parseFloat(e.target.value) : null })} placeholder="—"
                className="mt-1.5 w-full bg-[#0A0A0B] border border-white/10 rounded-lg px-2.5 py-2 text-sm outline-none focus:border-primary" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider font-semibold text-zinc-500">Departamento</label>
              <select data-testid="task-department-select" value={task.department_id || ""} onChange={(e) => save({ department_id: e.target.value || null })}
                className="mt-1.5 w-full bg-[#0A0A0B] border border-white/10 rounded-lg px-2.5 py-2 text-sm outline-none focus:border-primary">
                <option value="">—</option>
                {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </div>
          </div>

          {isInProgress && (
            <div data-testid="progress-section">
              <div className="flex items-center justify-between">
                <label className="text-xs uppercase tracking-wider font-semibold text-zinc-500">Avance</label>
                <span className="text-sm font-bold text-[#FFD600]" data-testid="progress-value">{task.progress || 0}%</span>
              </div>
              <input type="range" min="0" max="100" step="5" value={task.progress || 0}
                onChange={(e) => setTask({ ...task, progress: +e.target.value })}
                onMouseUp={(e) => save({ progress: +e.target.value })}
                onTouchEnd={(e) => save({ progress: +e.target.value })}
                data-testid="progress-slider"
                className="w-full accent-[#FFD600] mt-2 cursor-pointer" />
              <div className="h-1.5 bg-white/5 rounded-full overflow-hidden mt-1">
                <div className="h-full bg-[#FFD600] rounded-full transition-all" style={{ width: `${task.progress || 0}%` }} />
              </div>
            </div>
          )}

          {(board.custom_fields || []).length > 0 && (
            <div>
              <label className="text-xs uppercase tracking-wider font-semibold text-zinc-500">Campos personalizados</label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-2">
                {board.custom_fields.map((f) => (
                  <div key={f.id}>
                    <label className="text-[11px] text-zinc-500">{f.name}</label>
                    {f.type === "select" ? (
                      <select data-testid={`custom-field-${f.id}`} value={(task.custom_values || {})[f.id] || ""}
                        onChange={(e) => save({ custom_values: { ...(task.custom_values || {}), [f.id]: e.target.value } })}
                        className="mt-1 w-full bg-[#0A0A0B] border border-white/10 rounded-lg px-2.5 py-2 text-sm outline-none focus:border-primary">
                        <option value="">—</option>
                        {(f.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
                      </select>
                    ) : (
                      <input data-testid={`custom-field-${f.id}`} type={f.type === "number" ? "number" : f.type === "date" ? "date" : "text"}
                        defaultValue={(task.custom_values || {})[f.id] || ""}
                        onBlur={(e) => save({ custom_values: { ...(task.custom_values || {}), [f.id]: e.target.value } })}
                        className="mt-1 w-full bg-[#0A0A0B] border border-white/10 rounded-lg px-2.5 py-2 text-sm outline-none focus:border-primary [color-scheme:dark]" />
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          <Dependencies task={task} boardId={board.id} onSave={save} />

          {/* Assignees */}
          <div>
            <label className="text-xs uppercase tracking-wider font-semibold text-zinc-500">Responsables</label>
            <div className="flex flex-wrap gap-2 mt-2">
              {users.map((u) => {
                const active = (task.assignee_ids || []).includes(u.id);
                return (
                  <button key={u.id} onClick={() => toggleAssignee(u.id)} data-testid={`assignee-toggle-${u.id}`}
                    className={`flex items-center gap-2 pl-1 pr-3 py-1 rounded-full border transition-all ${active ? "border-primary bg-primary/10" : "border-white/10 hover:border-white/25"}`}>
                    <Avatar user={u} size={22} />
                    <span className="text-xs">{u.name.split(" ")[0]}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Labels */}
          <div>
            <label className="text-xs uppercase tracking-wider font-semibold text-zinc-500 flex items-center gap-1.5"><Tag className="h-3 w-3" /> Etiquetas</label>
            <div className="flex flex-wrap items-center gap-2 mt-2">
              {(task.labels || []).map((l) => (
                <span key={l} className="text-xs font-semibold px-2.5 py-1 rounded-full flex items-center gap-1.5" style={{ background: `${labelColor(l)}22`, color: labelColor(l) }}>
                  {l}<button onClick={() => removeLabel(l)} className="hover:opacity-70"><X className="h-3 w-3" /></button>
                </span>
              ))}
              <div className="flex items-center gap-1">
                <input data-testid="label-input" value={newLabel} onChange={(e) => setNewLabel(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addLabel())} placeholder="+ etiqueta"
                  className="bg-[#0A0A0B] border border-white/10 rounded-full px-3 py-1 text-xs w-28 outline-none focus:border-primary" />
              </div>
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="text-xs uppercase tracking-wider font-semibold text-zinc-500">Descripción</label>
            <textarea data-testid="task-desc-input" value={task.description || ""} onChange={(e) => setTask({ ...task, description: e.target.value })} onBlur={(e) => save({ description: e.target.value })} rows={3} placeholder="Añade más detalles…"
              className="mt-1.5 w-full bg-[#0A0A0B] border border-white/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-primary resize-none" />
          </div>

          {/* Subtasks */}
          <div>
            <label className="text-xs uppercase tracking-wider font-semibold text-zinc-500 flex items-center gap-1.5"><CheckSquare className="h-3 w-3" /> Subtareas {(task.subtasks || []).length > 0 && `(${doneSubs}/${task.subtasks.length})`}</label>
            <div className="space-y-1.5 mt-2">
              {(task.subtasks || []).map((s, si) => (
                <div key={s.id} className="flex items-center gap-2 group bg-[#0A0A0B] rounded-lg px-3 py-2 animate-fade-up" style={{ animationDelay: `${Math.min(si * 40, 350)}ms` }}>
                  <input type="checkbox" checked={s.done} onChange={() => toggleSub(s.id)} data-testid={`subtask-check-${s.id}`} className="accent-[#00E676] h-4 w-4" />
                  <span className={`flex-1 text-sm ${s.done ? "line-through text-zinc-600" : ""}`}>{s.title}</span>
                  <select value={s.assignee_id || ""} data-testid={`subtask-assignee-${s.id}`}
                    onChange={(e) => save({ subtasks: task.subtasks.map((x) => x.id === s.id ? { ...x, assignee_id: e.target.value || null } : x) })}
                    className="bg-transparent text-xs text-zinc-500 outline-none max-w-[90px] cursor-pointer hover:text-white">
                    <option value="">Nadie</option>
                    {users.map((u) => <option key={u.id} value={u.id}>{u.name.split(" ")[0]}</option>)}
                  </select>
                  <button onClick={() => removeSub(s.id)} className="opacity-0 group-hover:opacity-100 text-zinc-500 hover:text-red-400"><X className="h-4 w-4" /></button>
                </div>
              ))}
              <div className="flex gap-2">
                <input data-testid="subtask-input" value={newSub} onChange={(e) => setNewSub(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addSub())} placeholder="Añadir subtarea…"
                  className="flex-1 bg-[#0A0A0B] border border-white/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-primary" />
                <button onClick={addSub} data-testid="add-subtask-button" className="bg-white/5 hover:bg-white/10 rounded-lg px-3"><Plus className="h-4 w-4" /></button>
              </div>
            </div>
          </div>

          <TimeTracker task={task} />

          <Attachments taskId={task.id} />

          {/* Comments & Activity */}
          <div>
            <div className="flex gap-1 border-b border-white/10">
              <button onClick={() => setTab("comments")} data-testid="tab-comments"
                className={`flex items-center gap-1.5 text-xs uppercase tracking-wider font-semibold px-3 py-2 border-b-2 -mb-px transition-colors ${tab === "comments" ? "border-primary text-white" : "border-transparent text-zinc-500 hover:text-white"}`}>
                <MessageSquare className="h-3 w-3" /> Comentarios{comments.length > 0 && ` (${comments.length})`}
              </button>
              <button onClick={() => setTab("activity")} data-testid="tab-activity"
                className={`flex items-center gap-1.5 text-xs uppercase tracking-wider font-semibold px-3 py-2 border-b-2 -mb-px transition-colors ${tab === "activity" ? "border-primary text-white" : "border-transparent text-zinc-500 hover:text-white"}`}>
                <History className="h-3 w-3" /> Actividad
              </button>
            </div>
            {tab === "activity" ? (
              <div className="space-y-3 mt-4" data-testid="activity-list">
                {activity.map((a) => (
                  <div key={a.id} className="flex items-start gap-3" data-testid={`activity-${a.id}`}>
                    <span className="h-2 w-2 rounded-full mt-1.5 shrink-0" style={{ background: a.user_color || "#FF3B30" }} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-zinc-300"><span className="font-semibold text-white">{a.user_name}</span> {a.action}</p>
                      <p className="text-xs text-zinc-600">{new Date(a.created_at).toLocaleString("es", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</p>
                    </div>
                  </div>
                ))}
                {activity.length === 0 && <p className="text-sm text-zinc-600">Sin actividad registrada.</p>}
              </div>
            ) : (
              <>
                <div className="space-y-3 mt-3">
                  {comments.map((c, ci) => (
                    <div key={c.id} className="flex gap-3 animate-fade-up" style={{ animationDelay: `${Math.min(ci * 40, 350)}ms` }} data-testid={`comment-${c.id}`}>
                      <Avatar user={c.user} size={30} />
                      <div className="flex-1 bg-[#0A0A0B] rounded-lg px-3 py-2">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold">{c.user?.name}</span>
                          <span className="text-xs text-zinc-600">{new Date(c.created_at).toLocaleDateString("es")}</span>
                        </div>
                        <p className="text-sm text-zinc-300 mt-0.5 whitespace-pre-wrap">{renderText(c.text)}</p>
                        <div className="flex flex-wrap items-center gap-1.5 mt-2">
                          {Object.entries(c.reactions || {}).map(([emoji, ids]) => (
                            <button key={emoji} onClick={() => react(c.id, emoji)} data-testid={`reaction-${c.id}-${emoji}`}
                              className={`text-xs px-2 py-0.5 rounded-full border transition-colors ${ids.includes(user?.id) ? "border-primary bg-primary/15" : "border-white/10 bg-white/5 hover:border-white/25"}`}>
                              {emoji} {ids.length}
                            </button>
                          ))}
                          {reactingId === c.id ? (
                            <span className="flex gap-1 bg-[#1C1C1F] border border-white/10 rounded-full px-2 py-0.5">
                              {QUICK_EMOJIS.map((e2) => (
                                <button key={e2} onClick={() => react(c.id, e2)} data-testid={`pick-reaction-${c.id}-${e2}`} className="text-sm hover:scale-125 transition-transform">{e2}</button>
                              ))}
                            </span>
                          ) : (
                            <button onClick={() => setReactingId(c.id)} data-testid={`add-reaction-${c.id}`}
                              className="text-xs text-zinc-600 hover:text-white px-1.5 py-0.5 rounded-full border border-dashed border-white/10 transition-colors">+</button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                  {comments.length === 0 && <p className="text-sm text-zinc-600">Sé el primero en comentar.</p>}
                </div>
                <form onSubmit={addComment} className="relative flex gap-2 mt-3">
                  {mentionMatches.length > 0 && (
                    <div className="absolute bottom-full mb-1 left-10 bg-[#1C1C1F] border border-white/10 rounded-xl overflow-hidden shadow-xl z-10" data-testid="mention-dropdown">
                      {mentionMatches.map((u) => (
                        <button type="button" key={u.id} onClick={() => pickMention(u)} data-testid={`mention-option-${u.id}`}
                          className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-white/5">
                          <Avatar user={u} size={22} /> {u.name}
                        </button>
                      ))}
                    </div>
                  )}
                  <Avatar user={user} size={30} />
                  <input data-testid="comment-input" value={newComment} onChange={onCommentChange} placeholder="Escribe un comentario… (@ para mencionar)"
                    className="flex-1 bg-[#0A0A0B] border border-white/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-primary" />
                  <button type="submit" disabled={posting} data-testid="comment-submit" className="bg-primary hover:bg-[#FF5047] rounded-lg px-3 flex items-center disabled:opacity-60">
                    {posting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
      {confirmDelete && (
        <ConfirmDialog
          title="Eliminar tarea"
          message={`¿Eliminar «${task.title}»? Podrás restaurarla desde la papelera de Administración.`}
          confirmLabel="Eliminar tarea"
          onConfirm={del}
          onCancel={() => setConfirmDelete(false)}
        />
      )}
      {warnMsg && (
        <ConfirmDialog title="Acción no permitida" message={warnMsg} confirmLabel="Entendido" cancelLabel={null} tone="warn"
          onConfirm={() => setWarnMsg(null)} onCancel={() => setWarnMsg(null)} />
      )}
      {pendingStatus && (
        <ConfirmDialog title="¿Iniciar tarea?"
          message="La tarea tomará la fecha de hoy como inicio, desaparecerá del estado «Planeado» y ya no podrá regresar al estado inicial. ¿Estás seguro?"
          confirmLabel="Sí, iniciar" tone="warn"
          onConfirm={() => { save({ status: pendingStatus }); setPendingStatus(null); }}
          onCancel={() => setPendingStatus(null)} />
      )}
    </div>
  );
}
